"use strict";
'use es6';

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard");

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

var _sizes = require("HubStyleTokens/sizes");

var _propTypes = _interopRequireDefault(require("prop-types"));

var _react = _interopRequireWildcard(require("react"));

var _reactDom = require("react-dom");

var _useUniqueId = _interopRequireDefault(require("react-utils/hooks/useUniqueId"));

var _useFactory = _interopRequireDefault(require("../hooks/useFactory"));

var _Dom = require("../utils/Dom");

var _domElement = _interopRequireDefault(require("../utils/propTypes/domElement"));

var _RootNode = require("../utils/RootNode");

var _Timers = require("../utils/Timers");

var _Layer = require("./utils/Layer");

var _Placement = require("./utils/Placement");

var _ChildrenWrapper = _interopRequireDefault(require("../utils/ChildrenWrapper"));

var UIAbstractFloaty = (0, _react.forwardRef)(function (props, ref) {
  var bodyElement = props.bodyElement,
      children = props.children,
      computePosition = props.computePosition,
      content = props.content,
      layer = props.layer,
      onPositionChange = props.onPositionChange,
      target = props.target,
      zIndex = props.zIndex;
  /** The ID used for the layer element */

  var uniqueId = (0, _useUniqueId.default)("uiabstractfloaty-");
  /** The DOM element where the layer element will be mounted */

  var rootNode = bodyElement || (0, _RootNode.getDefaultNode)();
  /** The DOM element where the content will be rendered */

  var layerElement = (0, _useFactory.default)(function () {
    return layer || (0, _Layer.makeFloatyLayerElement)(rootNode, uniqueId, zIndex);
  });
  /** A ref to the wrapper around `children` */

  var _useState = (0, _react.useState)(),
      _useState2 = (0, _slicedToArray2.default)(_useState, 2),
      childrenWrapperRef = _useState2[0],
      setChildrenWrapperRef = _useState2[1];
  /** The DOM element the floaty is attached to (`target` if given, `children` otherwise) */


  var targetElement = target || (0, _reactDom.findDOMNode)(childrenWrapperRef); // Persist the latest targetElement in a ref

  var targetElementRef = (0, _react.useRef)();
  targetElementRef.current = targetElement; // Persist the latest `computePosition` callback in a ref

  var computePositionRef = (0, _react.useRef)();
  computePositionRef.current = computePosition; // Persist the latest `onPositionChange` callback in a ref

  var onPositionChangeRef = (0, _react.useRef)();
  onPositionChangeRef.current = onPositionChange;
  /** Call `computePosition` and move the `layerElement` */

  var reposition = function reposition() {
    if (!targetElementRef.current) return;
    var targetBounds = (0, _Dom.getRelativeRect)(targetElementRef.current, layerElement.offsetParent);
    var contentDimensions = (0, _Placement.getElementDimensions)(layerElement);
    var position = computePositionRef.current({
      targetBounds: targetBounds,
      contentDimensions: contentDimensions
    });
    var positionTransform = (0, _Placement.getTransformForPosition)(position);

    if (layerElement.style.transform !== positionTransform) {
      layerElement.style.transform = positionTransform;
      if (onPositionChangeRef.current) onPositionChangeRef.current();
    }
  }; // Expose `reposition()` as a public method


  (0, _react.useImperativeHandle)(ref, function () {
    return {
      reposition: reposition
    };
  }); // Recompute the layer element's position immediately after every render

  (0, _react.useLayoutEffect)(reposition); // Bind event handlers that trigger reposition

  (0, _react.useEffect)(function () {
    if (!targetElement) return undefined; // Debounce repositioning triggered by events to prevent scroll jank

    var debouncedReposition = (0, _Timers.debounceByTick)(reposition); // Reposition whenever DOM mutations occur within the content layer

    var mutationObserver = new MutationObserver(function (mutationRecords) {
      // Ignore observed mutations that were caused by `reposition()` itself.
      if (mutationRecords.every(function (mutationRecord) {
        return mutationRecord.target === layerElement;
      })) {
        return;
      }

      debouncedReposition();
    });
    mutationObserver.observe(layerElement, {
      attributes: true,
      childList: true,
      subtree: true
    }); // Reposition whenever image `load` events occur in the target element

    targetElement.addEventListener('load', debouncedReposition, true); // Reposition on window-level scroll/resize

    var surroundingWindows = (0, _Dom.getIframeAwareSurroundingWindows)(targetElement);
    surroundingWindows.forEach(function (surroundingWindow) {
      surroundingWindow.addEventListener('resize', debouncedReposition);
      surroundingWindow.addEventListener('scroll', debouncedReposition);
    }); // Reposition on scroll in any ancestor of the target

    var ancestorElements = (0, _Dom.getIframeAwareAncestorElements)(targetElement);
    ancestorElements.forEach(function (ancestorElement) {
      ancestorElement.addEventListener('scroll', debouncedReposition);
    }); // Set `data-popover-id` on the target

    (0, _Dom.addToListAttr)(targetElement, 'data-popover-id', uniqueId);
    return function () {
      mutationObserver.disconnect();
      targetElement.removeEventListener('load', debouncedReposition, true);
      surroundingWindows.forEach(function (surroundingWindow) {
        surroundingWindow.removeEventListener('resize', debouncedReposition);
        surroundingWindow.removeEventListener('scroll', debouncedReposition);
      });
      ancestorElements.forEach(function (ancestorElement) {
        ancestorElement.removeEventListener('scroll', debouncedReposition);
      });
      (0, _Dom.removeFromListAttr)(targetElement, 'data-popover-id', uniqueId);
    };
  }, [targetElement]); // eslint-disable-line react-hooks/exhaustive-deps
  // Remove the layer element on unmount

  (0, _react.useEffect)(function () {
    return function () {
      layerElement.parentElement.removeChild(layerElement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return _react.default.createElement(_react.Fragment, null, children && _react.default.createElement(_ChildrenWrapper.default, {
    ref: setChildrenWrapperRef
  }, children), targetElement && content && (0, _reactDom.createPortal)(content, layerElement));
});
UIAbstractFloaty.displayName = 'UIAbstractFloaty';
UIAbstractFloaty.propTypes = {
  bodyElement: _domElement.default,
  children: _propTypes.default.node,
  computePosition: _propTypes.default.func.isRequired,
  content: _propTypes.default.node,
  layer: _domElement.default,

  /** An event handler that gets invoked when the layer is repositioned */
  onPositionChange: _propTypes.default.func,
  target: _domElement.default,
  zIndex: _propTypes.default.number.isRequired
};
UIAbstractFloaty.defaultProps = {
  zIndex: parseInt(_sizes.JUPITER_LAYER, 10)
};
var _default = UIAbstractFloaty;
exports.default = _default;
module.exports = exports.default;